import { createTheme } from './createTheme';
import { DARK_MODE, GRAY_SCALE } from 'constants/ThemeConstant';
import { rgba, createOpaqueTableRowColors, buildOpaqueTableRowScopedStyles, getTableRowTokens, getColoredHeaderTableTokens, buildThemeTableSorterScopedStyles, buildThemeCheckboxScopedStyles, buildThemeGroupRowCssVariables, buildThemePaginationScopedStyles, getThemePaginationTokens } from './utils';

const LIBERATION_PRIMARY = '#F5222D';
const LIBERATION_BG = '#FFFBE6';
const LIBERATION_TEXT = '#5C0011';
const LIBERATION_BORDER = '#FFE58F';
const LIBERATION_GOLD = rgba('#FFD700', 0.9);
/** Icon module-menu — vàng đậm, tương phản trên nền đỏ */
const LIBERATION_MODULE_ICON = '#E6B800';
const LIBERATION_TABLE_ROW_COLORS = createOpaqueTableRowColors(LIBERATION_PRIMARY, GRAY_SCALE.WHITE);

/** Sidebar custom-menu — không dùng token Menu global */
export const liberationCustomMenuStyles = `
.side-nav .custom-menu .ant-menu-item:not(.ant-menu-item-selected),
.side-nav .custom-menu .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open) > .ant-menu-submenu-title {
    color: ${LIBERATION_TEXT} !important;
}
.side-nav .custom-menu .ant-menu-item:not(.ant-menu-item-selected) .anticon,
.side-nav .custom-menu .ant-menu-item:not(.ant-menu-item-selected) .ant-menu-item-icon,
.side-nav .custom-menu .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open) > .ant-menu-submenu-title .anticon,
.side-nav .custom-menu .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open) > .ant-menu-submenu-title .ant-menu-item-icon {
    color: ${LIBERATION_MODULE_ICON} !important;
}
.side-nav .custom-menu .ant-menu-item-group-title {
    color: ${LIBERATION_GOLD} !important;
}
.custom-menu-popup.ant-menu-dark .ant-menu-item:not(.ant-menu-item-selected),
.custom-menu-popup.ant-menu-dark .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open):not(.ant-menu-submenu-active) > .ant-menu-submenu-title {
    color: ${GRAY_SCALE.WHITE} !important;
}
.custom-menu-popup.ant-menu-dark .ant-menu-item:not(.ant-menu-item-selected) .anticon,
.custom-menu-popup.ant-menu-dark .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open) > .ant-menu-submenu-title .anticon {
    color: ${LIBERATION_MODULE_ICON} !important;
}
`;

/** Steps điều hướng form (className="step-nav") */
export const liberationStepNavStyles = `
.step-nav.ant-steps .ant-steps-item-title {
    color: ${LIBERATION_TEXT} !important;
    font-size: 13px !important;
}
.step-nav.ant-steps .ant-steps-item-process .ant-steps-item-title,
.step-nav.ant-steps .ant-steps-item-finish .ant-steps-item-title {
    color: ${LIBERATION_PRIMARY} !important;
    font-weight: 600 !important;
}
.step-nav.ant-steps .ant-steps-item-process .ant-steps-item-icon,
.step-nav.ant-steps .ant-steps-item-finish .ant-steps-item-icon {
    background-color: transparent !important;
    border-color: ${LIBERATION_PRIMARY} !important;
}
.step-nav.ant-steps .ant-steps-item-process .ant-steps-item-icon .ant-steps-icon,
.step-nav.ant-steps .ant-steps-item-process .ant-steps-item-icon .anticon,
.step-nav.ant-steps .ant-steps-item-finish .ant-steps-item-icon .ant-steps-icon,
.step-nav.ant-steps .ant-steps-item-finish .ant-steps-item-icon .anticon {
    color: ${LIBERATION_PRIMARY} !important;
}
.step-nav.ant-steps .ant-steps-item-wait .ant-steps-item-icon {
    background-color: transparent !important;
    border-color: ${LIBERATION_BORDER} !important;
}
.step-nav.ant-steps .ant-steps-item-wait .ant-steps-item-icon .ant-steps-icon,
.step-nav.ant-steps .ant-steps-item-wait .ant-steps-item-icon .anticon {
    color: ${GRAY_SCALE.GRAY} !important;
}
`;

export const liberationScopedStyles = `
${liberationCustomMenuStyles}
${liberationStepNavStyles}
${buildThemePaginationScopedStyles(LIBERATION_GOLD, LIBERATION_BORDER)}
.ant-table-wrapper .ant-table-thead > tr > th {
    font-weight: 600;
    text-transform: uppercase;
}
${buildThemeTableSorterScopedStyles(LIBERATION_PRIMARY)}
${buildOpaqueTableRowScopedStyles(LIBERATION_TABLE_ROW_COLORS)}
${buildThemeCheckboxScopedStyles('light')}
${buildThemeGroupRowCssVariables(LIBERATION_PRIMARY, LIBERATION_GOLD, 'light', LIBERATION_BG)}
`;

export const liberationTheme = createTheme({
    primaryColor: LIBERATION_PRIMARY,
    successColor: '#FAAD14',
    warningColor: '#FFD700',
    errorColor: '#FF4D4F',

    bgColor: LIBERATION_BG,
    textColor: LIBERATION_TEXT,
    borderColor: LIBERATION_BORDER,
    bodyBg: LIBERATION_BG,
    colorBgLayout: LIBERATION_BG,

    menuGroupTitleColor: LIBERATION_GOLD,
    moduleMenuBg: LIBERATION_PRIMARY,
    moduleMenuIconColor: LIBERATION_MODULE_ICON,
    moduleMenuItemColor: GRAY_SCALE.WHITE,
    moduleMenuItemSelectedBg: GRAY_SCALE.WHITE,
    moduleMenuItemSelectedColor: LIBERATION_PRIMARY,
    headerNavColor: LIBERATION_BG,
    siderBg: GRAY_SCALE.WHITE,
    customMenuBg: GRAY_SCALE.WHITE,
    customMenuItemSelectedColor: LIBERATION_TEXT,
    customMenuItemSelectedBg: LIBERATION_BG,
    customMenuItemSelectedIconColor: LIBERATION_PRIMARY,
    customMenuItemSelectedBorderColor: LIBERATION_GOLD,

    footerBg: LIBERATION_PRIMARY,
    footerTextColor: GRAY_SCALE.WHITE,

    componentOverrides: {
        Layout: {
            bodyBg: LIBERATION_BG,
            headerBg: LIBERATION_BG,
            siderBg: GRAY_SCALE.WHITE,
        },
        Menu: {
            darkItemBg: 'transparent',
            darkItemColor: GRAY_SCALE.WHITE,
            darkItemSelectedBg: DARK_MODE.ACTIVE_BG_COLOR,
            darkItemSelectedColor: GRAY_SCALE.WHITE,
            darkItemHoverBg: DARK_MODE.HOVER_BG_COLOR,
            darkItemHoverColor: GRAY_SCALE.GRAY_DARK,
            darkSubMenuItemBg: DARK_MODE.BG_COLOR,
            popupBg: DARK_MODE.BG_COLOR,
            activeBarWidth: 0,
            boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
        },
        Breadcrumb: {
            itemColor: GRAY_SCALE.GRAY,
            lastItemColor: LIBERATION_PRIMARY,
            linkColor: GRAY_SCALE.GRAY,
            linkHoverColor: LIBERATION_PRIMARY,
            separatorColor: GRAY_SCALE.GRAY,
        },
        Card: {
            colorBgContainer: GRAY_SCALE.WHITE,
            colorTextHeading: LIBERATION_PRIMARY,
            colorText: LIBERATION_TEXT,
            colorBorderSecondary: LIBERATION_BORDER,
            extraColor: LIBERATION_TEXT,
            headerBg: GRAY_SCALE.WHITE,
            actionsBg: GRAY_SCALE.WHITE,
            borderRadiusLG: 22,
        },
        Table: {
            colorText: LIBERATION_TEXT,
            colorTextHeading: GRAY_SCALE.WHITE,
            headerBg: LIBERATION_PRIMARY,
            borderColor: '#F0F0F0',
            ...getColoredHeaderTableTokens(LIBERATION_PRIMARY),
            ...getTableRowTokens(LIBERATION_TABLE_ROW_COLORS),
        },
        Steps: {
            colorPrimary: LIBERATION_PRIMARY,
            colorText: LIBERATION_TEXT,
            colorTextDescription: GRAY_SCALE.GRAY,
        },
        Input: {
            colorBgContainer: GRAY_SCALE.WHITE,
            colorBorder: LIBERATION_BORDER,
            activeBorderColor: LIBERATION_PRIMARY,
            hoverBorderColor: LIBERATION_PRIMARY,
        },
        Pagination: getThemePaginationTokens(LIBERATION_PRIMARY, LIBERATION_BORDER),
    },
});
